import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { userRequest } from '../../utils/requestMethods';
import { FiEdit, FiMoreHorizontal, FiEye } from 'react-icons/fi';
import { Header, CustomPagination, StatusBadge, BackButton, SkeletonTable, TableDropdown, StudentIDModal } from '../../components';
import { useStateContext } from '../../contexts/ContextProvider';
import * as Icons from '../../data/class';
import { MdSearch } from 'react-icons/md';

const customStyles = {
  headRow: {
    style: {
      backgroundColor: 'rgb(248, 250, 252)',
      fontWeight: "bold",
    },
  },
};

const TeacherStudents = ({ history }) => {
  const location = useLocation();
  const classId = location.pathname.split('/')[3];
  const { school } = useStateContext();
  const schoolId = school?._id;
  
  const [students, setStudents] = useState([]);
  const [activeStudents, setActiveStudents] = useState(0);
  const [inactiveStudents, setInactiveStudents] = useState(0);
  const [classInfo, setClassInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [isStudentIDModalOpen, setIsStudentIDModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    if (classId && schoolId) {
      fetchStudents();
      fetchClass();
    }
  }, [classId, schoolId]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const { data } = await userRequest.get(`children/class/${classId}?limit=200`);
      console.log(data.children);
      setStudents(data.children);
      
      // Calculate active and inactive students
      const active = data.children.filter(student => student.academic_status === 'active' && student.is_active);
      const inactive = data.children.filter(student => student.academic_status !== 'active' || !student.is_active);

      setActiveStudents(active.length);
      setInactiveStudents(inactive.length);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClass = async () => {
    try {
      const { data } = await userRequest.get(`classes/${schoolId}/${classId}`);
      setClassInfo(data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDropdownClick = (rowIndex, event) => {
    const rect = event.target.getBoundingClientRect();
    setDropdownIndex(dropdownIndex === rowIndex ? null : rowIndex);
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleCloseDropdown = () => setDropdownIndex(null);

  const openStudentIDModal = (student) => {
    setSelectedStudent(student);
    setIsStudentIDModalOpen(true);
    handleCloseDropdown();
  };

  const closeStudentIDModal = () => {
    setIsStudentIDModalOpen(false);
    setSelectedStudent(null);
  };

  const columns = [
    {
      name: "Admission Number",
      selector: row => row.registration_id,
      sortable: true,
    },
    {
      name: "Last Name",
      selector: row => row.lastname,
      sortable: true,
    },
    {
      name: "First Name",
      selector: row => row.firstname,
      sortable: true,
    },
    {
      name: "Gender",
      selector: row => row.gender,
      sortable: true,
    },
    {
      name: "Account (collection)",
      selector: row => (
        <>
          {row.account ? (
            <div>
              <p>{row.account.account_number}</p>
              <p>{row.account.name}</p>
            </div>
          ) : (
            <p>Account not assigned</p>
          )}
        </>
      ),
    },
    {
      name: "Status",
      selector: row => (
        <div>
          {row.academic_status ? (
            <StatusBadge status="active" />
          ) : (
            <StatusBadge status="inactive" />
          )}
        </div>
      ),
    },
    {
      cell: (row, rowIndex) => (
        <div className="relative">
          <button
            className="p-2 text-gray-600 hover:text-gray-900"
            onClick={e => handleDropdownClick(rowIndex, e)}
          >
            <FiMoreHorizontal className="text-2xl" />
          </button>
          <TableDropdown
            isVisible={dropdownIndex === rowIndex}
            position={dropdownPosition}
            options={[
              {
                label: "View Student ID",
                icon: <img src={Icons.checkList} alt="ID Icon" />,
                onClick: () => openStudentIDModal(row),
              },
              {
                label: "Student Profile",
                icon: <FiEye className="w-4 h-4" />,
                onClick: () => {
                  history.push(`/students/view/${row._id}/${classId}`, {
                    student: `${row.firstname} ${row.lastname}`,
                  });
                },
              },
              {
                label: "Edit Student",
                icon: <FiEdit className="w-4 h-4" />,
                onClick: () => {
                  history.push(`/update-student/${row._id}`, { student: row });
                },
              },
            ]}
            onClose={handleCloseDropdown}
          />
        </div>
      ),
      width: "120px",
    },
  ];

  const filteredData = students
    ?.filter((item) =>
      (item.firstname?.toLowerCase().includes(filterText.toLowerCase()) || '') ||
      (item.lastname?.toLowerCase().includes(filterText.toLowerCase()) || '') 
    )
    .sort((a, b) => {
      const surnameA = a.lastname || '';
      const surnameB = b.lastname || '';
      return surnameA.localeCompare(surnameB);
    });

  return (
    <>
      <div className="px-10 pt-6">
        <BackButton history={history} />
      </div>
      <div className="px-10 pt-5">
        <Header category="Teacher Module" title={classInfo?.class_name || "Loading..."} />
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-2 m-2 md:m-2 mt-5 p-2 md:p-5">
        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Total Students</p>
              <p className="text-2xl font-semibold">{students.length}</p>
            </div>
            <button type="button" className="bg-[#3371E8] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4">
              <img src={Icons.userMultiple} alt="Wallet Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Active Students</p>
              <p className="text-2xl font-semibold">{activeStudents}</p>
            </div>
            <button type="button" className="bg-[#41D3BD] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4">
              <img src={Icons.coupon} alt="Payout Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Inactive Students</p>
              <p className="text-2xl font-semibold">{inactiveStudents}</p>
            </div>
            <button type="button" className="bg-[#E26E6A] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4">
              <img src={Icons.taskDaily} alt="Cashflow Icon" />
            </button>
          </div>
        </div>
      </div>

      <div className="m-2 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-lg font-semibold w-44">Students Details</h1>
          <div className="flex justify-center md:justify-end w-full">
            <form className="max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 start-0 flex items-center pl-4 pointer-events-none">
                  <MdSearch />
                </div>
                <input
                  type="text"
                  id="email-address-icon"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-md rounded-full focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
                  placeholder="Search"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </form>
          </div>
        </div>
        <div className="border rounded-3xl">
          <DataTable
            data={filteredData}
            columns={columns}
            getRowId={(row) => row._id}
            pagination
            paginationComponent={CustomPagination}
            paginationRowsPerPageOptions={[10, 20, 30, 50]}
            customStyles={customStyles}
            progressPending={loading}
            progressComponent={<SkeletonTable rows={5} columns={6} />}
            highlightOnHover
            striped
          />
        </div>
      </div>

      {/* Student ID Modal */}
      {isStudentIDModalOpen && selectedStudent && (
        <StudentIDModal
          isOpen={isStudentIDModalOpen}
          onClose={closeStudentIDModal}
          student={selectedStudent}
          school={school}
          classInfo={classInfo}
        />
      )}
    </>
  );
};

export default TeacherStudents;

